import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useField, useFormState } from 'react-final-form'
import { MODAL_IDS, ModalItem, useModal } from 'components'
import { FieldFormItem, FieldRadio, required } from 'forms'
import { checkFieldError, getFieldErrorMessage } from 'helpers/form'
import { useAttachContactByCustomerIdMutation, useGetContactsByCustomerIdQuery } from 'modules/customer/api'
import { CustomerAddModal } from 'modules/customer/components'
import { OrderClientBlock, OrderCreateContactModal, OrderCreateInfoBlock } from 'modules/order/components'
import { useOrderCreateFormData } from 'modules/order/pages'
import { RadioSwitchValue, TOrderCreateFormData, TOrderCreatePartnerFormData } from 'modules/order/types'
import { CourierDeliveryInfo, OrderItemClient } from 'schema'

import { FieldOrderCreateContactItemProps } from './types'

const FieldOrderCreateContactItem: FC<FieldOrderCreateContactItemProps> = (props) => {
  const { className, name, type, customerTypeValue, userContact, setIsPartnerUpdate, storeKey } = props
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [currentContact, setCurrentContact] = useState<TOrderCreatePartnerFormData | null>(null)
  const addCustomerModalId =
    name === 'departurePartner' ? MODAL_IDS.AddCustomerDeparture : MODAL_IDS.AddCustomerDestination
  const orderCreateContactModalId =
    name === 'departurePartner' ? MODAL_IDS.OrderCreateContactDeparture : MODAL_IDS.OrderCreateContactDestination

  const {
    activeCustomerId,
    setActiveCustomerId,
    setActiveCustomer,
    selectedCustomer,
    setIsContactsByCustomerList,
    setIsActiveCustomerAddContactAccess,
    setIsShowAttachBtn,
    setIsAttachBlockFullHeight
  } = useOrderCreateFormData()

  const { openModal, closeModal, modals } = useModal()
  const { values } = useFormState<TOrderCreateFormData>()
  const current = values[name]

  const { input, meta } = useField(name)
  const { initial } = meta
  const { input: typeInput } = useField<OrderItemClient.type>(`${name}.type`)
  const { input: isUserInput, meta: fieldIsUserMeta } = useField<OrderItemClient.type>(`${name}.isUser`)

  const [attachContactByCustomerId] = useAttachContactByCustomerIdMutation()
  const { refetch: refetchAttachedContacts } = useGetContactsByCustomerIdQuery(activeCustomerId || '', {
    skip: !activeCustomerId
  })

  const error = useMemo(() => {
    const partnerMetaError = checkFieldError(meta)
    const fieldIsUserMetaError = checkFieldError(fieldIsUserMeta)

    return getFieldErrorMessage(
      (typeof partnerMetaError !== 'object' && partnerMetaError) ||
        (typeof fieldIsUserMetaError !== 'object' && fieldIsUserMetaError)
    )
  }, [fieldIsUserMeta, meta])

  const handleAttach = useCallback(
    (customerId: string, contactId: string) => {
      attachContactByCustomerId &&
        // @ts-ignore
        attachContactByCustomerId({ customerId, id: contactId }).then(() => {
          setIsShowAttachBtn(true)
          refetchAttachedContacts && refetchAttachedContacts()
          setIsAttachBlockFullHeight && setIsAttachBlockFullHeight(false)
        })
    },
    [attachContactByCustomerId, refetchAttachedContacts, setIsAttachBlockFullHeight, setIsShowAttachBtn]
  )

  const handleChange = useCallback(
    (data: TOrderCreatePartnerFormData) => {
      if (!data) {
        return
      }

      input.onChange(data)
      setCurrentContact(data)
    },
    [input, setCurrentContact]
  )

  const handleClose = useCallback(() => {
    const { companyId, contactId } = currentContact || {}

    if (!(companyId || contactId)) {
      setIsPartnerUpdate(true)
      input.onChange({
        isUser: undefined,
        type: OrderItemClient.type.LEGAL,
        contactId: undefined,
        companyId: undefined
      })
    }

    setIsOpen(false)
    closeModal(orderCreateContactModalId)
  }, [closeModal, currentContact, input, orderCreateContactModalId, setIsPartnerUpdate])

  const handleOpenCustomerAddModal = useCallback(() => {
    openModal(addCustomerModalId)
  }, [addCustomerModalId, openModal])

  const handleOpenContactModal = useCallback(() => {
    setIsOpen(true)
    openModal(orderCreateContactModalId)
  }, [openModal, orderCreateContactModalId])

  const isMyContact = useMemo(() => {
    if (current?.isUser === RadioSwitchValue.yes) {
      if (customerTypeValue === OrderItemClient.type.LEGAL && !!selectedCustomer?.id) {
        return true
      }
      if (customerTypeValue === OrderItemClient.type.INDIVIDUAL) {
        return true
      }
    }

    return false
  }, [current?.isUser, customerTypeValue, selectedCustomer?.id])

  const isAnotherContact = current?.isUser === RadioSwitchValue.no && Boolean(current?.type?.length)
  const hasCustomer =
    current?.isUser === RadioSwitchValue.yes &&
    customerTypeValue === OrderItemClient.type.LEGAL &&
    !selectedCustomer?.id

  const label = useMemo(() => {
    if (customerTypeValue === OrderItemClient.type.LEGAL) {
      return 'Я как представитель заказчика'
    }

    if (type === CourierDeliveryInfo.pointType.DEPARTURE) {
      return 'Я отправитель'
    }

    return 'Я получатель'
  }, [customerTypeValue, type])

  const clientType = useMemo(
    () => (current?.isUser === RadioSwitchValue.yes ? customerTypeValue : currentContact?.type),
    [current?.isUser, currentContact?.type, customerTypeValue]
  )

  const handleChangeIsUser = useCallback(() => {
    setIsPartnerUpdate(true)
  }, [setIsPartnerUpdate])

  useEffect(() => {
    if (typeInput.value && modals.some((item) => item.id === orderCreateContactModalId)) {
      setCurrentContact(null)
      setActiveCustomerId('')
    }
  }, [setActiveCustomerId, modals, typeInput.value, isUserInput.value])

  return (
    <div className={className}>
      <ModalItem id={addCustomerModalId}>
        <CustomerAddModal
          onClose={() => closeModal(addCustomerModalId)}
          setActiveCustomer={setActiveCustomer}
          setActiveCustomerId={setActiveCustomerId}
          setIsActiveCustomerAddContactAccess={setIsActiveCustomerAddContactAccess}
          setIsContactsByCustomerList={setIsContactsByCustomerList}
          storeKey={storeKey}
        />
      </ModalItem>

      <ModalItem id={orderCreateContactModalId}>
        <OrderCreateContactModal
          handleAttach={handleAttach}
          handleOpenCustomerAddModal={handleOpenCustomerAddModal}
          id={orderCreateContactModalId}
          onChange={handleChange}
          onClose={handleClose}
          onPartnerTypeChange={typeInput.onChange}
          pointType={type}
          setIsPartnerUpdate={setIsPartnerUpdate}
        />
      </ModalItem>

      <div className="mb-3 mt-4 text-headline-sm">
        {type === CourierDeliveryInfo.pointType.DEPARTURE ? 'Отправитель' : 'Получатель'}
      </div>
      <FieldFormItem className="inline-flex flex-col gap-y-1" name={`${name}.isUser`}>
        <FieldRadio
          name={`${name}.isUser`}
          onPick={handleChangeIsUser}
          validate={required}
          value={RadioSwitchValue.yes}
        >
          {label}
        </FieldRadio>
        {hasCustomer && <span className="text-body-sm font-normal text-negative">Выберите заказчика</span>}
        <FieldRadio
          name={`${name}.isUser`}
          onPick={handleOpenContactModal}
          validate={required}
          value={RadioSwitchValue.no}
        >
          Другое лицо
        </FieldRadio>
        {error && <span className="text-body-sm font-normal text-negative">{error}</span>}
      </FieldFormItem>
      {isMyContact && (
        <OrderClientBlock
          orderClientCompany={!selectedCustomer && initial ? initial.company : selectedCustomer}
          orderClientContact={!userContact && initial ? initial.contact : userContact}
          orderClientType={customerTypeValue}
        />
      )}
      {isAnotherContact && (currentContact?.company || currentContact?.contact || initial) && (
        <OrderClientBlock
          isShowSelect
          handleShowSelect={handleOpenContactModal}
          orderClientCompany={!currentContact && initial ? initial.company : currentContact?.company}
          orderClientContact={!currentContact && initial ? initial.contact : currentContact?.contact}
          orderClientType={!clientType && initial?.type ? initial?.type : clientType}
        />
      )}
      {(isMyContact || isAnotherContact) && !isOpen && (
        <OrderCreateInfoBlock clientType={clientType} pointType={type} />
      )}
    </div>
  )
}

export default memo(FieldOrderCreateContactItem)
